import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Brain, ArrowLeft, Eye, EyeOff, HelpCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import zxcvbn from 'zxcvbn';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface SignupForm {
  fullName: string;
  email: string;
  role: 'admin' | 'staff';
  schoolCode: string;
  password: string;
}

export default function SignupPage() {
  useDocumentTitle('Sign Up');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSchoolCodeInfo, setShowSchoolCodeInfo] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [form, setForm] = useState<SignupForm>({
    fullName: '',
    email: '',
    role: 'staff',
    schoolCode: searchParams.get('code') || '',
    password: '',
  });

  // Validate school code on mount if provided in URL
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      validateSchoolCode(code);
    }
  }, []);

  const getPasswordStrength = (password: string) => {
    const result = zxcvbn(password);
    return {
      score: result.score,
      feedback: result.feedback.warning || result.feedback.suggestions[0],
    };
  };

  const passwordStrength = getPasswordStrength(form.password);

  const getStrengthColor = (score: number) => {
    switch (score) {
      case 0: return 'bg-error-DEFAULT';
      case 1: return 'bg-error-DEFAULT';
      case 2: return 'bg-warning-DEFAULT';
      case 3: return 'bg-success-DEFAULT';
      case 4: return 'bg-success-DEFAULT';
      default: return 'bg-border-light';
    }
  };

  const validateSchoolCode = async (code: string) => {
    if (!code.trim()) return false;
    
    try {
      setValidatingCode(true);
      const schoolQuery = query(
        collection(db, 'schools'),
        where('code', '==', code.trim())
      );
      const querySnapshot = await getDocs(schoolQuery);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error validating school code:', error);
      return false;
    } finally {
      setValidatingCode(false);
    }
  };

  const validateForm = async () => {
    if (!form.fullName.trim()) {
      setError('Full name is required');
      return false;
    }
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (passwordStrength.score < 2) {
      setError('Please choose a stronger password');
      return false;
    }
    if (form.role === 'staff' && !form.schoolCode.trim()) {
      setError('School code is required for staff members');
      return false;
    }
    if (form.role === 'staff') {
      const isValidCode = await validateSchoolCode(form.schoolCode);
      if (!isValidCode) {
        setError('Invalid school code. Please check with your administrator');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      console.log('Starting signup process...', {
        email: form.email,
        role: form.role,
        schoolCode: form.schoolCode
      });

      setError('');
      setLoading(true);

      if (!await validateForm()) {
        setLoading(false);
        return;
      }

      // Create Firebase Auth user
      console.log('Creating Firebase auth user...');
      const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
      console.log('Auth user created:', userCredential.user.uid);
      
      // Create user profile in Firestore
      console.log('Creating user profile...');
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        fullName: form.fullName,
        email: form.email,
        role: form.role,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'online',
        lastActive: new Date().toISOString()
      });
      console.log('User profile created');

      // If staff member with valid code, add to school members
      if (form.role === 'staff' && form.schoolCode) {
        console.log('Attempting to add staff to school:', form.schoolCode);
        const schoolQuery = query(
          collection(db, 'schools'),
          where('code', '==', form.schoolCode.trim())
        );
        const querySnapshot = await getDocs(schoolQuery);
        
        if (!querySnapshot.empty) {
          const schoolDoc = querySnapshot.docs[0];
          console.log('Found matching school:', schoolDoc.id);
          
          const membershipId = `${schoolDoc.id}_${userCredential.user.uid}`;
          console.log('Creating school membership:', membershipId);
          
          await setDoc(doc(db, 'school_members', membershipId), {
            userId: userCredential.user.uid,
            schoolId: schoolDoc.id,
            role: 'staff',
            joinedAt: new Date().toISOString(),
          });
          console.log('School membership created');
        } else {
          console.log('No matching school found for code:', form.schoolCode);
        }
      } else {
        console.log('Skipping school membership - user is admin or no school code provided');
      }

      navigate('/onboarding');
    } catch (err) {
      console.error('Signup error:', err);
      const message = err instanceof Error ? err.message : 'Failed to create account';
      if (message.includes('email-already-in-use')) {
        setError('An account with this email already exists');
      } else if (message.includes('invalid-email')) {
        setError('Please enter a valid email address');
      } else if (message.includes('weak-password')) {
        setError('Password should be at least 6 characters');
      } else {
        setError(message);
      }
      toast.error('Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-light to-background-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center text-text-secondary hover:text-primary-500 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        <div className="bg-surface-light rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Brain className="w-8 h-8 text-primary-500" />
            <h1 className="text-2xl font-bold text-text-primary">Campus Connect</h1>
          </div>

          <h2 className="text-2xl font-semibold text-text-primary text-center mb-6">
            Create your account
          </h2>

          {error && (
            <div className="bg-error-light/20 text-error-dark px-4 py-2 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-text-secondary mb-1">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                placeholder="you@school.edu"
                required
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-text-secondary mb-1">
                Role
              </label>
              <select
                id="role"
                value={form.role}
                onChange={(e) => {
                  const newRole = e.target.value as 'admin' | 'staff';
                  setForm({ 
                    ...form, 
                    role: newRole,
                    // Clear school code if switching to admin
                    schoolCode: newRole === 'admin' ? '' : form.schoolCode 
                  });
                }}
                className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                required
              >
                <option value="staff">Staff/Teacher</option>
                <option value="admin">School Administrator</option>
              </select>
            </div>

            {form.role === 'staff' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="schoolCode" className="block text-sm font-medium text-text-secondary">
                    School Code
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowSchoolCodeInfo(!showSchoolCodeInfo)}
                    className="text-text-secondary hover:text-primary-500 transition-colors"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                {showSchoolCodeInfo && (
                  <div className="mb-2 p-2 bg-background rounded-lg text-sm text-text-secondary">
                    <p className="mb-1">Enter the school code provided by your administrator to join your school's dashboard.</p>
                    <p>You can change your school later in your profile settings.</p>
                  </div>
                )}
                <input
                  id="schoolCode"
                  type="text"
                  value={form.schoolCode}
                  onChange={(e) => setForm({ ...form, schoolCode: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  placeholder="Enter your school code"
                  required
                />
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all pr-10"
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {form.password && (
                <div className="mt-2">
                  <div className="h-1 w-full bg-border-light rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${getStrengthColor(passwordStrength.score)}`}
                      style={{ width: `${(passwordStrength.score + 1) * 20}%` }}
                    />
                  </div>
                  {passwordStrength.feedback && (
                    <p className="text-xs text-text-secondary mt-1">
                      {passwordStrength.feedback}
                    </p>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || validatingCode}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 
                       rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-text-secondary">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}